import React from 'react'

import { SubscriptionTierDto } from '@/shared/apis/fastmile/models/generated'
import { pxToRem } from '@/shared/styles/themes'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { faCheckCircle, faChevronDown, faTimesCircle } from '@fortawesome/pro-solid-svg-icons'
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, styled, Typography } from '@mui/material'

import { Button } from '../Button/Button' // Assuming Button is in ../Button/Button.tsx relative to this file
import { Icon } from '../Icon/Icon' // Assuming Icon is in ../Icon/Icon.tsx

export type FeatureDetail = {
  title: string
  details: string
  available: 'yes' | 'no' | 'neutral'
}

export type SubscriptionTierProps = {
  tier: SubscriptionTierDto
  description: string
  features: FeatureDetail[]
  billingInterval: 'monthly' | 'yearly'
  onSelect: (tier: SubscriptionTierDto) => void
  isRecommended?: boolean
  isDisabled?: boolean
  actionButtonText?: string
  currencySymbol?: string
}

export const SubscriptionTier: React.FC<SubscriptionTierProps> = ({
  tier,
  description,
  features,
  billingInterval,
  onSelect,
  isRecommended = false,
  isDisabled = false,
  actionButtonText,
  currencySymbol = 'AUD $',
}) => {
  const getPrice = () => {
    if (tier.name === 'Free' || !tier.pricing) return 0

    return billingInterval === 'monthly'
      ? tier.pricing.monthlyAmount || 0
      : tier.pricing.yearlyAmount || 0
  }

  const getPriceDisplay = () => {
    const price = getPrice()

    return `${currencySymbol}${price}`
  }

  const getPriceSubtitle = () => {
    const interval = billingInterval === 'monthly' ? 'month' : 'year'

    return `per run per ${interval}`
  }

  const getAvailableIcon = (availibility: 'yes' | 'no' | 'neutral') => {
    if (availibility === 'yes')
      return <Icon icon={faCheckCircle} color="success" sx={{ mr: pxToRem(6) }} />
    if (availibility === 'no')
      return <Icon icon={faTimesCircle} color="error" sx={{ mr: pxToRem(6) }} />

    return <Icon icon={faInfoCircle} color="info" sx={{ mr: pxToRem(6) }} />
  }

  const currentActionButtonText = actionButtonText
    ? actionButtonText
    : isRecommended
      ? 'Selected'
      : isDisabled
        ? 'Unavailable'
        : 'Select Plan'

  return (
    <StyledCard
      $isRecommended={isRecommended}
    >

      {isRecommended && (
        <StyledRecommendedFlag>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white' }}>
            Recommended
          </Typography>
        </StyledRecommendedFlag>
      )}
      <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <StyledTierInfoContainer>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            {tier.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ minHeight: '40px' }}> {/* Min height for alignment */}
            {description}
          </Typography>
        </StyledTierInfoContainer>

        <StyledPriceContainer>
          {tier.name === 'Enterprise' ? (
            <>
              <Typography variant="caption">
                Enterprise plan pricing is available when your fleet size is larger than 20 vehicles.
              </Typography>
              <Box sx={{ marginBottom: pxToRem(16) }}/>
            </>
          ) : (
            <>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'medium', fontSize: pxToRem(32) }}>
                {getPriceDisplay()}
              </Typography>

              <Typography variant="caption" color="text.secondary" display="block">
                {getPriceSubtitle()}
              </Typography>
            </>
          )}
        </StyledPriceContainer>

        <StyledSelectButton
          fullWidth
          primary={isRecommended && !isDisabled}
          outlined={!isRecommended || isDisabled}
          disabled={isDisabled}
          onClick={() => !isDisabled && onSelect(tier)}
          size={'medium'}
        >
          {currentActionButtonText}
        </StyledSelectButton>

        <StyledFeaturesAccordionContainer>
          {features.map((feature, index) => (
            <Accordion key={index} elevation={0} square disableGutters sx={{ backgroundColor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
              <StyledAccordionSummary
                expandIcon={<Icon icon={faChevronDown} />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                $isAvailable={feature.available}
              >
                {getAvailableIcon(feature.available)}
                <Typography variant="body2">{feature.title}</Typography>
              </StyledAccordionSummary>
              <AccordionDetails>
                <Typography color="text.primary">
                  {feature.details}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </StyledFeaturesAccordionContainer>
      </CardContent>
    </StyledCard>
  )
}

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => !['$isRecommended'].includes(prop as string),
}) <{
  $isRecommended: boolean
}>`
  position: relative;
  border-radius: ${pxToRem(8)};
  border: 1px solid ${(props) => props.theme.palette.divider};
  background-color: ${props => props.theme.palette.background.paper};
  box-shadow: none !important;
  transition: border-color 0.2s ease-in-out;

  ${props => props.$isRecommended && `
    border: 2px solid ${props.theme.palette.primary.main};
  `}
  
  &:hover {
    box-shadow: none !important;
    transform: none !important;
  }
`


const StyledRecommendedFlag = styled(Box)`
  position: absolute;
  top: ${pxToRem(-8)};
  left: 50%;
  transform: translateX(-50%);
  background-color: ${(props) => props.theme.palette.primary.main};
  color: white;
  padding: ${pxToRem(10)} ${pxToRem(16)} ${pxToRem(4)} ${pxToRem(16)};
  border-radius: ${pxToRem(8)};
  z-index: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`

const StyledSelectButton = styled(Button)`
  margin-bottom: ${(props) => props.theme.spacing(2)};
`

const StyledTierInfoContainer = styled(Box)`
  text-align: center;
  margin-top: ${(props) => props.theme.spacing(2)};
  margin-bottom: ${(props) => props.theme.spacing(2)};
`

const StyledPriceContainer = styled(Box)`
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing(3)};
`

const StyledFeaturesAccordionContainer = styled(Box)`
  margin-top: auto;
  padding-top: ${(props) => props.theme.spacing(1)};
  border-top: 1px solid ${(props) => props.theme.palette.divider};
`

const StyledAccordionSummary = styled(AccordionSummary, {
  shouldForwardProp: (prop) => prop !== '$isAvailable',
}) <{ $isAvailable: 'yes' | 'no' | 'neutral' }>`
  padding: ${(props) => props.theme.spacing(0, 1)};
  min-height: ${(props) => props.theme.spacing(5)};
  & .MuiAccordionSummary-content {
    margin: ${(props) => props.theme.spacing(1, 0)};
    align-items: center;
  }
  color: ${(props) => props.$isAvailable ? props.theme.palette.text.disabled : 'inherit'};

  .MuiSvgIcon-root {
      font-size: ${props => props.theme.typography.pxToRem(16)};
  }
`